import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

export interface Options {
  title: string;
  value: string;
}

interface DropDownProps {
  selected: string | null | undefined;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
  options: Array<Options> | null | undefined;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

const Dropdown: React.FC<DropDownProps> = ({
  selected,
  setSelected,
  options = [],
  className = "",
  disabled = false,
  placeholder = "Select option...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 128,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    const openUpward = spaceBelow < 140 && spaceAbove > spaceBelow;

    const availableSpace = openUpward ? spaceAbove - 10 : spaceBelow - 10;
    const optimalMaxHeight = Math.min(Math.max(availableSpace, 100), 200);

    let left = buttonRect.left;
    const dropdownWidth = buttonRect.width;

    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 10;
    }
    if (left < 10) {
      left = 10;
    }

    setDropdownPosition({
      top: openUpward
        ? buttonRect.top - optimalMaxHeight - 5
        : buttonRect.bottom + 2,
      left: Math.max(left, 10),
      width: buttonRect.width,
      maxHeight: optimalMaxHeight,
    });
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;

    if (!isOpen) {
      calculatePosition();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const onOptionClick = (option: Options) => {
    if (option?.value) {
      setSelected(option.value);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("touchstart", handleClickOutside, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handlePositionUpdate = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      const scrollElements = [
        window,
        ...Array.from(document.querySelectorAll("[data-scroll-container]")),
        ...Array.from(
          document.querySelectorAll(
            ".overflow-auto, .overflow-scroll, .overflow-x-auto"
          )
        ),
      ];

      scrollElements.forEach((element) => {
        element.addEventListener("scroll", handlePositionUpdate, {
          passive: true,
        });
      });

      window.addEventListener("resize", handlePositionUpdate, {
        passive: true,
      });
      document.addEventListener("keydown", handleEscape);

      let resizeObserver: ResizeObserver | null = null;
      if (buttonRef.current && "ResizeObserver" in window) {
        resizeObserver = new ResizeObserver(handlePositionUpdate);
        resizeObserver.observe(buttonRef.current);
      }

      return () => {
        scrollElements.forEach((element) => {
          element.removeEventListener("scroll", handlePositionUpdate);
        });
        window.removeEventListener("resize", handlePositionUpdate);
        document.removeEventListener("keydown", handleEscape);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [isOpen, calculatePosition]);

  const selectedTitle =
    options?.find((e) => e?.value === selected)?.title || placeholder;

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      style={{
        position: "fixed",
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 999999,
        pointerEvents: "auto",
      }}
    >
      <ul
        role="listbox"
        className="overflow-y-auto rounded-md border border-pink-400 bg-white shadow-xl focus:outline-none"
        style={{
          maxHeight: dropdownPosition.maxHeight,
          boxShadow:
            "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
      >
        {options?.length === 0 ? (
          <li className="cursor-pointer px-3 py-2 text-sm text-gray-800 hover:bg-pink-100">
            No Options Found
          </li>
        ) : (
          options?.map((option) => (
            <li
              key={option?.value}
              className="cursor-pointer px-3 py-2 text-sm text-gray-800 hover:bg-pink-100 transition-colors"
              onClick={() => onOptionClick(option)}
              onMouseDown={(e) => e.preventDefault()}
            >
              {option?.title || "N.A"}
            </li>
          ))
        )}
      </ul>
    </div>
  );

  return (
    <div className={className}>
      <div className="relative select-none">
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={toggleDropdown}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleDropdown();
            }
          }}
          className={`
            w-full flex justify-between items-center px-3 py-2 text-sm rounded-md border transition-colors duration-200
            ${
              disabled
                ? "bg-gray-100 text-gray-400 border-[#EA33C0] cursor-not-allowed"
                : isOpen
                ? "bg-[#EA33C0] border-[#EA33C0] text-white shadow-md"
                : "bg-white border-pink-400 text-gray-400 hover:text-[#EA33C0] hover:border-[#EA33C0] focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            }
          `}
          disabled={disabled}
        >
          <span
            className={`block truncate max-w-full text-left ${
              selected
                ? "text-black/70 pt-1"
                : isOpen
                ? "text-white pt-1"
                : "text-gray-400 pt-1"
            }`}
          >
            {selectedTitle}
          </span>

          <svg
            className={`w-4 h-4 ml-2 transition-transform duration-200 flex-shrink-0 ${
              isOpen ? "rotate-180" : "rotate-0"
            }`}
            fill="none"
            stroke={isOpen ? "white" : "#EA33C0"}
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {typeof document !== "undefined" &&
          createPortal(dropdownContent, document.body)}
      </div>
    </div>
  );
};

export default Dropdown;
